import { v4 as uuidV4 } from "uuid";
import type { PdfField as DestinationPacketPdfField } from "../types";
import type { PdfFieldRect } from "@/section/pdf/types";
import { AcceptedDestinationPacketFields } from "@/lib/api/destinationPackets";

export interface PdfFieldMetaShape extends Record<string, unknown> {
  label?: string;
  value?: string;
  fontSize?: number;
  destinationPacket?: {
    fieldKey?: string;
    fieldType?: DestinationPacketPdfField["field_type"];
    systemFieldType?: DestinationPacketPdfField["system_field_type"];
    fieldDestType?: DestinationPacketPdfField["field_dest_type"];
    signerId?: string;
    isRequired?: boolean;
    isReadonly?: boolean;
  };
}

type Mutable<T> = {
  -readonly [K in keyof T]: T[K];
};

function normalizePage(page?: number | null) {
  if (typeof page === "number" && Number.isFinite(page) && page > 0) {
    return Math.floor(page);
  }
  return 1;
}

function normalizeRectCoordinates(field: DestinationPacketPdfField): Mutable<PdfFieldRect["rect"]> {
  const { position_data: data } = field;
  return {
    x: Number(data?.x ?? 0),
    y: Number(data?.y ?? 0),
    width: Number(data?.width ?? 0),
    height: Number(data?.height ?? 0),
  };
}

function toMeta(field: DestinationPacketPdfField): PdfFieldMetaShape {
  return {
    label: field.field_key ?? field.field_id,
    value: field.value ?? field.freeform_value ?? "",
    fontSize: field.position_data?.text_size,
    destinationPacket: {
      fieldKey: field.field_key,
      fieldType: field.field_type,
      systemFieldType: field.system_field_type,
      fieldDestType: field.field_dest_type,
      signerId: field.signer_id,
      isRequired: field.is_required,
      isReadonly: field.is_readonly,
    },
  };
}

export function destinationPacketFieldToPdfFieldRect(field: DestinationPacketPdfField): PdfFieldRect {
  return {
    id: field.field_id,
    page: normalizePage(field.position_data?.page),
    rect: normalizeRectCoordinates(field),
    meta: toMeta(field),
  };
}

export function destinationPacketFieldsToPdfFieldRects(fields: DestinationPacketPdfField[]): PdfFieldRect[] {
  return fields.map(destinationPacketFieldToPdfFieldRect);
}

function resolveDestinationPacketMeta(rect: PdfFieldRect): PdfFieldMetaShape["destinationPacket"] {
  const meta = rect.meta as PdfFieldMetaShape | undefined;
  return meta?.destinationPacket ?? {};
}

function resolveValue(rect: PdfFieldRect): string | undefined {
  const meta = rect.meta as PdfFieldMetaShape | undefined;
  return typeof meta?.value === "string" ? meta.value : undefined;
}

function resolveFontSize(rect: PdfFieldRect): number | undefined {
  const meta = rect.meta as PdfFieldMetaShape | undefined;
  return typeof meta?.fontSize === "number" ? meta.fontSize : undefined;
}

// Tracked signature fields that must preserve their exact IDs
const TRACKED_SIGNATURE_FIELDS = [
  AcceptedDestinationPacketFields.LENDER_NAME,
  AcceptedDestinationPacketFields.LENDER_EMAIL,
] as const;

function isTrackedSignatureField(fieldId: string): boolean {
  return TRACKED_SIGNATURE_FIELDS.includes(fieldId as typeof TRACKED_SIGNATURE_FIELDS[number]);
}

export function pdfFieldRectToDestinationPacketField(rect: PdfFieldRect, base?: Partial<DestinationPacketPdfField>): DestinationPacketPdfField {
  const destinationPacketMeta = resolveDestinationPacketMeta(rect);
  const value = resolveValue(rect);
  const page = normalizePage(rect.page);
  const fieldType = base?.field_type ?? destinationPacketMeta?.fieldType ?? "signature";
  
  let fieldId = rect.id;

  return {
    field_id: fieldId,
    field_key: base?.field_key ?? destinationPacketMeta?.fieldKey ?? rect.id,
    field_type: fieldType,
    system_field_type: base?.system_field_type ?? destinationPacketMeta?.systemFieldType,
    field_dest_type: base?.field_dest_type ?? destinationPacketMeta?.fieldDestType,
    signer_id: base?.signer_id ?? destinationPacketMeta?.signerId,
    position_data: {
      x: rect.rect.x,
      y: rect.rect.y,
      width: rect.rect.width,
      height: rect.rect.height,
      text_size: resolveFontSize(rect) ?? base?.position_data?.text_size,
      page,
    },
    is_required: base?.is_required ?? destinationPacketMeta?.isRequired,
    is_readonly: base?.is_readonly ?? destinationPacketMeta?.isReadonly,
    freeform_value: base?.freeform_value ?? value,
    value: base?.value ?? value,
  };
}

export function pdfFieldRectsToDestinationPacketFields(rects: PdfFieldRect[], existing: Record<string, DestinationPacketPdfField> = {}): DestinationPacketPdfField[] {
  return rects.map((rect) => pdfFieldRectToDestinationPacketField(rect, existing[rect.id]));
}

